import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { Modal } from '../components/Modal';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { useToast } from '../components/Toast';
import type { BatchYear, SemesterDto } from '../types/models';

/** "2024-06-01" -> "01-06-2024". */
function fmtDate(iso: string) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

const emptyForm = { name: '', startDate: '', endDate: '' };

/** Semesters for one batch year — auto-generated in 6-month blocks; each can be renamed / re-dated. */
export function SemesterPage() {
  const { orgId = '' } = useParams();
  const [params] = useSearchParams();
  const toast = useToast();
  const batchYearId = params.get('batchYearId') ?? '';

  const [batchYear, setBatchYear] = useState<BatchYear | null>(null);
  const [semesters, setSemesters] = useState<SemesterDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orgId, batchYearId]);

  async function refresh() {
    try {
      const [years, sems] = await Promise.all([api.listBatchYears(orgId), api.listSemesters(batchYearId)]);
      setBatchYear(years.find(y => y.id === batchYearId) ?? null);
      setSemesters(sems);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load semesters.');
    }
  }

  const ordered = useMemo(() => (semesters ?? []).slice().sort((a, b) => a.number - b.number), [semesters]);

  function openEdit(s: SemesterDto) {
    setFormError(null); setEditId(s.id);
    setForm({ name: s.name, startDate: s.startDate, endDate: s.endDate });
    setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!editId) return;
    setFormError(null);
    if (form.endDate < form.startDate) { setFormError('End date must be on or after the start date.'); return; }
    setBusy(true);
    try {
      await api.updateSemester(editId, { name: form.name.trim(), startDate: form.startDate, endDate: form.endDate });
      toast.success('Semester updated.');
      setShowModal(false);
      await refresh();
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save semester.');
    } finally { setBusy(false); }
  }

  async function regenerate() {
    if (!confirm('Regenerate semesters from the batch year dates? This replaces any manual edits.')) return;
    try {
      const sems = await api.regenerateSemesters(batchYearId);
      setSemesters(sems);
      toast.success('Semesters regenerated.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to regenerate.');
    }
  }

  return (
    <OrgLayout>
      <div style={{ marginBottom: 8 }}>
        <Link to={`/org/${orgId}/batch-years`} className="link-btn">← Batch Year</Link>
      </div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0 }}>Semesters</h1>
          <div className="muted" style={{ marginTop: 4 }}>
            {batchYear
              ? `${batchYear.name} · ${fmtDate(batchYear.startDate)} → ${fmtDate(batchYear.endDate)} · ${ordered.length} semesters`
              : 'Loading…'}
          </div>
        </div>
        <button onClick={regenerate} disabled={!batchYear}>Regenerate</button>
      </div>

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Semester</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th style={{ width: 160 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {!semesters && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
            {semesters && ordered.length === 0 && (
              <tr><td colSpan={5} className="muted">No semesters for this batch year.</td></tr>
            )}
            {ordered.map(s => (
              <tr key={s.id}>
                <td>{s.number}</td>
                <td style={{ fontWeight: 600 }}>{s.name}</td>
                <td>{fmtDate(s.startDate)}</td>
                <td>{fmtDate(s.endDate)}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(s)}>Edit</button>
                  <RecordInfoButton entity="semesters" id={s.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title="Edit semester" onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <label>
              <div className="muted">Semester name *</div>
              <input value={form.name} required maxLength={100} autoFocus
                     onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Semester 1" />
            </label>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Start Date *</div>
                <input type="date" value={form.startDate} required
                       onChange={e => setForm({ ...form, startDate: e.target.value })} />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">End Date *</div>
                <input type="date" value={form.endDate} required
                       onChange={e => setForm({ ...form, endDate: e.target.value })} />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
